/* eslint-disable react-hooks/exhaustive-deps, react-hooks/immutability, @typescript-eslint/no-unused-vars */
import { faClose, faSave, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CurrencyTextField } from "../../../components";
import { useForm } from "../../../components/FormProvider";
import { Child, Receipt, ReceiptLine, ServicePeriod } from "../../../models";
import { formatNumberAsCurrency, getChildImageUrlFromChild, parseDate } from "../../../utils";

interface ReceiptLineRow {
  childId: number;
  childName: string;
  childAvatarId: number;
  weeks: number;
  amount: string;
  included: boolean;
  existingLineId: number;
  createdCentralDateTime: string;
}

interface BuildInitialStateResult {
  paymentMethodChoice: string;
  paymentMethodOther: string;
  checkNumber: string;
  adjustmentEnabled: boolean;
  adjustmentAmount: string;
  adjustmentDescription: string;
  lines: ReceiptLineRow[];
}

export interface ReceiptFormData {
  receipt: Receipt;
}

interface EditReceiptFormProps {
  mode: "create" | "edit";
  servicePeriod: ServicePeriod | null;
  receipt: Receipt | null;
  children: Child[];
  isLoading: boolean;
  resetFormRef: React.RefObject<(() => void) | null>;
  dataGetterRef: React.RefObject<(() => ReceiptFormData | null) | null>;
}

const PAYMENT_METHOD_OPTIONS = ["Cash", "Check", "PayPal", "Venmo", "Zelle", "Other"];

function startOfWeekMonday(value: Date): Date {
  const dayOfWeek = value.getDay();
  const daysSinceMonday = (dayOfWeek - 1 + 7) % 7;
  const date = new Date(value);
  date.setDate(date.getDate() - daysSinceMonday);
  return date;
}

function countBusinessWeeksEnrolled(child: Child, periodStart: Date, periodEnd: Date): number {
  const enrollmentStart = child.enrollmentStartCentralDateTime ? new Date(child.enrollmentStartCentralDateTime) : new Date(-8640000000000000);
  const enrollmentEnd = child.enrollmentEndCentralDateTime ? new Date(child.enrollmentEndCentralDateTime) : new Date(8640000000000000);

  const effectiveStart = periodStart > enrollmentStart ? periodStart : enrollmentStart;
  const effectiveEnd = periodEnd < enrollmentEnd ? periodEnd : enrollmentEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  let count = 0;
  let weekStart = startOfWeekMonday(effectiveStart);

  while (weekStart <= effectiveEnd) {
    const businessStart = weekStart;
    const businessEnd = new Date(weekStart);
    businessEnd.setDate(businessEnd.getDate() + 4);

    const overlapStart = effectiveStart > businessStart ? effectiveStart : businessStart;
    const overlapEnd = effectiveEnd < businessEnd ? effectiveEnd : businessEnd;

    if (overlapStart <= overlapEnd) {
      count++;
    }

    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return count;
}

function parseCurrencyInput(value: string): number {
  const normalized = value.replace(/[^0-9.-]/g, "").trim();
  if (!normalized) {
    return 0;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getPaymentMethodState(receipt: Receipt | null): { choice: string; other: string } {
  if (!receipt?.paymentMethod) {
    return { choice: "Zelle", other: "" };
  }

  const normalizedChoice = PAYMENT_METHOD_OPTIONS.find((option) => option.toLowerCase() === receipt.paymentMethod.toLowerCase());
  if (normalizedChoice) {
    return { choice: normalizedChoice, other: "" };
  }

  return { choice: "Other", other: receipt.paymentMethod };
}

function buildInitialState(servicePeriod: ServicePeriod | null, receipt: Receipt | null, children: Child[]): BuildInitialStateResult {
  const paymentMethodState = getPaymentMethodState(receipt);
  const adjustmentAmount = receipt?.paymentAdjustmentAmount ?? 0;

  if (!servicePeriod) {
    return {
      paymentMethodChoice: paymentMethodState.choice,
      paymentMethodOther: paymentMethodState.other,
      checkNumber: receipt?.checkNumber ?? "",
      adjustmentEnabled: adjustmentAmount !== 0 || !!receipt?.paymentAdjustmentDescription,
      adjustmentAmount: adjustmentAmount.toString(),
      adjustmentDescription: receipt?.paymentAdjustmentDescription ?? "",
      lines: [],
    };
  }

  const startDate = parseDate(servicePeriod.startCentralDateTime);
  const endDate = parseDate(servicePeriod.endCentralDateTime);

  const childById = new Map<number, Child>();
  children.forEach((child) => childById.set(child.id, child));

  const existingLinesByChildId = new Map<number, ReceiptLine>();
  (receipt?.lines ?? [])
    .filter((line) => !line.deleted)
    .forEach((line) => {
      existingLinesByChildId.set(line.childId, line);
    });

  const rows: ReceiptLineRow[] = [];
  const addedChildIds = new Set<number>();

  servicePeriod.lines
    .filter((line) => !line.deleted)
    .forEach((line) => {
      const child = line.child ?? childById.get(line.childId);
      if (!child) {
        return;
      }

      const weeks = startDate && endDate ? countBusinessWeeksEnrolled(child, startDate, endDate) : 0;
      const defaultAmount = (child.weeklyRate ?? 0) * weeks;
      const existingLine = existingLinesByChildId.get(child.id);

      rows.push({
        childId: child.id,
        childName: `${child.firstName} ${child.lastName}`.trim() || child.firstName,
        childAvatarId: child.id,
        weeks,
        amount: (existingLine?.amount ?? defaultAmount).toString(),
        included: !!existingLine || !receipt,
        existingLineId: existingLine?.id ?? 0,
        createdCentralDateTime: existingLine?.createdCentralDateTime ?? "2000-01-01",
      });

      addedChildIds.add(child.id);
    });

  // Keep existing receipt lines that are not in current service period lines so editing does not silently drop them.
  (receipt?.lines ?? [])
    .filter((line) => !line.deleted && !addedChildIds.has(line.childId))
    .forEach((line) => {
      const child = childById.get(line.childId);
      rows.push({
        childId: line.childId,
        childName: child ? `${child.firstName} ${child.lastName}`.trim() || child.firstName : `Child ${line.childId}`,
        childAvatarId: line.childId,
        weeks: startDate && endDate && child ? countBusinessWeeksEnrolled(child, startDate, endDate) : 0,
        amount: line.amount.toString(),
        included: true,
        existingLineId: line.id,
        createdCentralDateTime: line.createdCentralDateTime ?? "2000-01-01",
      });
    });

  return {
    paymentMethodChoice: paymentMethodState.choice,
    paymentMethodOther: paymentMethodState.other,
    checkNumber: receipt?.checkNumber ?? "",
    adjustmentEnabled: adjustmentAmount !== 0 || !!receipt?.paymentAdjustmentDescription,
    adjustmentAmount: adjustmentAmount.toString(),
    adjustmentDescription: receipt?.paymentAdjustmentDescription ?? "",
    lines: rows,
  };
}

export function EditReceiptForm(props: EditReceiptFormProps) {
  const { mode, servicePeriod, receipt, children, isLoading } = props;
  const form = useForm();

  const [paymentMethodChoice, setPaymentMethodChoice] = useState<string>("Zelle");
  const [paymentMethodOther, setPaymentMethodOther] = useState<string>("");
  const [checkNumber, setCheckNumber] = useState<string>("");
  const [adjustmentEnabled, setAdjustmentEnabled] = useState<boolean>(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>("0");
  const [adjustmentDescription, setAdjustmentDescription] = useState<string>("");
  const [lines, setLines] = useState<ReceiptLineRow[]>([]);

  const resetForm = (): void => {
    const initial = buildInitialState(servicePeriod, receipt, children);
    setPaymentMethodChoice(initial.paymentMethodChoice);
    setPaymentMethodOther(initial.paymentMethodOther);
    setCheckNumber(initial.checkNumber);
    setAdjustmentEnabled(initial.adjustmentEnabled);
    setAdjustmentAmount(initial.adjustmentAmount);
    setAdjustmentDescription(initial.adjustmentDescription);
    setLines(initial.lines);
  };

  useEffect(() => {
    resetForm();
  }, [servicePeriod, receipt, children]);

  const includedLines = useMemo(() => lines.filter((line) => line.included), [lines]);
  const linesTotal = useMemo(() => includedLines.reduce((sum, line) => sum + parseCurrencyInput(line.amount), 0), [includedLines]);
  const adjustmentNumeric = useMemo(() => (adjustmentEnabled ? parseCurrencyInput(adjustmentAmount) : 0), [adjustmentEnabled, adjustmentAmount]);
  const receiptTotal = useMemo(() => linesTotal + adjustmentNumeric, [linesTotal, adjustmentNumeric]);

  const childById = useMemo(() => {
    const map = new Map<number, Child>();
    (children ?? []).forEach((child) => map.set(child.id, child));
    return map;
  }, [children]);

  const getUpdatedRecord = (): ReceiptFormData | null => {
    if (!servicePeriod) {
      return null;
    }

    const paymentMethod = paymentMethodChoice === "Other" ? paymentMethodOther.trim() : paymentMethodChoice;
    if (!paymentMethod) {
      return null;
    }

    const selectedLines = lines.filter((line) => line.included);
    if (selectedLines.length === 0) {
      return null;
    }

    const nowFallback = "2000-01-01";
    const computedPaymentAmount = selectedLines.reduce((sum, line) => sum + parseCurrencyInput(line.amount), 0);

    return {
      receipt: {
        id: receipt?.id ?? 0,
        familyId: servicePeriod.familyId,
        serviceBeginCentralDateTime: servicePeriod.startCentralDateTime,
        serviceEndCentralDateTime: servicePeriod.endCentralDateTime,
        checkNumber: paymentMethodChoice === "Check" ? checkNumber.trim() : "",
        paymentAmount: computedPaymentAmount,
        paymentAdjustmentAmount: adjustmentEnabled ? parseCurrencyInput(adjustmentAmount) : 0,
        paymentAdjustmentDescription: adjustmentEnabled ? adjustmentDescription.trim() : "",
        paymentMethod,
        deleted: false,
        createdCentralDateTime: receipt?.createdCentralDateTime ?? nowFallback,
        modifiedCentralDateTime: receipt?.modifiedCentralDateTime ?? nowFallback,
        lines: selectedLines.map((line) => ({
          id: line.existingLineId,
          receiptId: receipt?.id ?? 0,
          childId: line.childId,
          amount: parseCurrencyInput(line.amount),
          deleted: false,
          createdCentralDateTime: line.createdCentralDateTime,
          modifiedCentralDateTime: nowFallback,
        })),
      },
    };
  };

  useEffect(() => {
    props.dataGetterRef.current = getUpdatedRecord;
    props.resetFormRef.current = resetForm;
  });

  const handlePaymentMethodChange = (event: SelectChangeEvent<string>) => {
    setPaymentMethodChoice(event.target.value);
  };

  const setLineIncluded = (index: number, included: boolean) => {
    setLines((prev) => prev.map((line, lineIndex) => (lineIndex === index ? { ...line, included } : line)));
  };

  const setLineAmount = (index: number, amount: string) => {
    setLines((prev) => prev.map((line, lineIndex) => (lineIndex === index ? { ...line, amount } : line)));
  };

  const formValidationError = useMemo(() => {
    if (!servicePeriod) {
      return "Service period is required.";
    }

    if (paymentMethodChoice === "Other" && !paymentMethodOther.trim()) {
      return "Enter a payment method when Other is selected.";
    }

    if (lines.filter((line) => line.included).length === 0) {
      return "Select at least one receipt line.";
    }

    return null;
  }, [servicePeriod, paymentMethodChoice, paymentMethodOther, lines]);

  if (isLoading && mode === "edit") {
    return (
      <Stack gap={2}>
        <Skeleton height={40} width={220} />
        <Skeleton height={56} width="100%" />
        <Skeleton height={240} width="100%" />
      </Stack>
    );
  }

  return (
    <Stack gap={2} flexGrow={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {mode === "create" ? "Create Receipt" : "Edit Receipt"}
        </Typography>
        <IconButton sx={{ width: 44, height: 44 }} onClick={form.cancel}>
          <FontAwesomeIcon icon={faClose} />
        </IconButton>
      </Stack>

      <Stack gap={2} flexGrow={1} sx={{ overflowY: "auto", pr: 1 }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={1}>
          <FormControl fullWidth variant="filled" disabled={form.isDisabled}>
            <InputLabel id="receipt-payment-method-label">Payment Method</InputLabel>
            <Select
              labelId="receipt-payment-method-label"
              label="Payment Method"
              value={paymentMethodChoice}
              onChange={handlePaymentMethodChange}
              MenuProps={{
                slotProps: {
                  root: { sx: { zIndex: 1600 } },
                  paper: { sx: { zIndex: 1600 } },
                },
              }}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {paymentMethodChoice === "Other" && (
            <TextField
              fullWidth
              variant="filled"
              label="Custom Payment Method"
              value={paymentMethodOther}
              onChange={(event) => setPaymentMethodOther(event.target.value)}
              disabled={form.isDisabled}
            />
          )}

          {paymentMethodChoice === "Check" && (
            <TextField
              variant="filled"
              label="Check #"
              value={checkNumber}
              onChange={(event) => setCheckNumber(event.target.value)}
              disabled={form.isDisabled}
              sx={{ width: { xs: "100%", md: 180 } }}
            />
          )}
        </Stack>

        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: "grey.100" }}>
            <Typography variant="subtitle2">Receipt Lines</Typography>
          </Box>

          <Stack>
            {lines.map((line, index) => {
              const lineAmount = parseCurrencyInput(line.amount);
              return (
                <Stack
                  key={`${line.childId}-${line.existingLineId}-${index}`}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{ px: 1.5, py: 1.25, borderTop: index === 0 ? 0 : 1, borderTopColor: "divider" }}
                >
                  <Checkbox checked={line.included} onChange={(event) => setLineIncluded(index, event.target.checked)} disabled={form.isDisabled} />
                  <Avatar alt={line.childName} src={getChildImageUrlFromChild(childById.get(line.childId))} sx={{ width: 28, height: 28 }} />
                  <Box sx={{ minWidth: 160, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {line.childName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "block", md: "none" } }}>
                      {line.weeks} week{line.weeks !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 84, color: "text.secondary", display: { xs: "none", md: "block" } }}>
                    {line.weeks} week{line.weeks !== 1 ? "s" : ""}
                  </Typography>
                  <CurrencyTextField
                    variant="filled"
                    size="small"
                    value={line.amount}
                    onChange={(event) => setLineAmount(index, event.target.value)}
                    disabled={form.isDisabled || !line.included}
                    sx={{ width: 100 }}
                    decimalPoints={2}
                  />
                </Stack>
              );
            })}

            <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 1.5, py: 1.25, borderTop: 1, borderTopColor: "divider", bgcolor: "grey.50" }}>
              <Checkbox checked={adjustmentEnabled} onChange={(event) => setAdjustmentEnabled(event.target.checked)} disabled={form.isDisabled} />
              <TextField
                variant="filled"
                size="small"
                placeholder="Adjustment description"
                value={adjustmentDescription}
                onChange={(event) => setAdjustmentDescription(event.target.value)}
                disabled={form.isDisabled || !adjustmentEnabled}
                sx={{ flexGrow: 1 }}
              />
              <CurrencyTextField
                variant="filled"
                size="small"
                value={adjustmentAmount}
                onChange={(event) => setAdjustmentAmount(event.target.value)}
                disabled={form.isDisabled || !adjustmentEnabled}
                sx={{ width: 100 }}
                decimalPoints={2}
              />
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderTop: 1, borderTopColor: "divider", bgcolor: "grey.100" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {formatNumberAsCurrency(receiptTotal)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {formValidationError && (
          <Typography variant="body2" color="error">
            {formValidationError}
          </Typography>
        )}
      </Stack>

      <Button
        variant="contained"
        onClick={form.save}
        disabled={form.isDisabled || !!formValidationError}
        sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
        startIcon={<FontAwesomeIcon icon={faSave} />}
      >
        Save
      </Button>

      <Button
        variant="outlined"
        onClick={form.cancel}
        disabled={form.isDisabled}
        sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
        startIcon={<FontAwesomeIcon icon={faTimes} />}
      >
        Cancel
      </Button>
    </Stack>
  );
}
